import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import Papa from "papaparse";
import { Download, Search, ExternalLink, Upload } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminApi,
  type AdminSchoolRow,
  type ListSchoolsInput,
  type SchoolImportRow,
  type BulkImportSchoolsOutput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";

export const Route = createFileRoute("/_authed/schools")({
  component: SchoolsPage,
});

const STATUSES = ["", "active", "inactive"] as const;
const SORTS = [
  { value: "name", label: "Name (A→Z)" },
  { value: "recent", label: "Recently updated" },
  { value: "labelCount", label: "Most labels" },
] as const;
const PAGE_SIZE = 50;

function SchoolsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [postcodePrefix, setPostcodePrefix] = useState("");
  // Typed filters key a server query: settle them before fetching.
  const debouncedSearch = useDebouncedValue(search);
  const debouncedPostcodePrefix = useDebouncedValue(postcodePrefix);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("");
  const [sortBy, setSortBy] = useState<(typeof SORTS)[number]["value"]>("name");
  const [importDialog, setImportDialog] = useState<{
    rows: SchoolImportRow[];
    fileName: string;
  } | null>(null);
  const [importPreview, setImportPreview] = useState<BulkImportSchoolsOutput | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filters: ListSchoolsInput = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      postcodePrefix: debouncedPostcodePrefix.trim() || undefined,
      status: status || undefined,
      sortBy,
      limit: PAGE_SIZE,
    }),
    [debouncedSearch, debouncedPostcodePrefix, status, sortBy],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.schools.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listSchools()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.schools) ?? [];
  const totalMatching = query.data?.pages[0]?.totalMatching ?? 0;

  const columns = useMemo<ColumnDef<AdminSchoolRow, unknown>[]>(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.name}</span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {row.original.id}
            </span>
          </div>
        ),
      },
      {
        header: "Postcode",
        accessorKey: "postcode",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.postcode ?? "—"}</span>
        ),
      },
      {
        header: "Contact",
        accessorKey: "contactEmail",
        cell: ({ row }) =>
          row.original.contactEmail ? (
            <div className="flex flex-col">
              <span className="text-xs">{row.original.contactEmail}</span>
              {row.original.contactPhone ? (
                <span className="text-[10px] text-muted-foreground">
                  {row.original.contactPhone}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Website",
        accessorKey: "website",
        cell: ({ row }) =>
          row.original.website ? (
            <a
              href={
                row.original.website.startsWith("http")
                  ? row.original.website
                  : `https://${row.original.website}`
              }
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.website.replace(/^https?:\/\//, "")}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Status",
        accessorKey: "isActive",
        cell: ({ row }) =>
          row.original.isActive ? (
            <StatusPill tone="success">active</StatusPill>
          ) : (
            <StatusPill tone="neutral">inactive</StatusPill>
          ),
      },
      {
        header: "Drop-offs",
        accessorKey: "acceptsDropoffs",
        cell: ({ row }) =>
          row.original.acceptsDropoffs ? (
            <StatusPill tone="info">yes</StatusPill>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        header: "Labels",
        accessorKey: "labelCount",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.labelCount.toLocaleString()}</span>
        ),
      },
      {
        header: "Updated",
        accessorKey: "lastUpdated",
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.lastUpdated
              ? format(new Date(row.original.lastUpdated), "yyyy-MM-dd")
              : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const dryRunMutation = useMutation({
    mutationFn: async (rows: SchoolImportRow[]) =>
      (await adminApi.bulkImportSchools()({ schools: rows, dryRun: true })).data,
    onSuccess: (res) => setImportPreview(res),
  });

  const commitMutation = useMutation({
    mutationFn: async (rows: SchoolImportRow[]) =>
      (await adminApi.bulkImportSchools()({ schools: rows, dryRun: false })).data,
    onSuccess: (res) => {
      setImportPreview(res);
      void qc.invalidateQueries({ queryKey: queryKeys.schools.all });
    },
  });

  function onImportFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? "");
        const rows = parseImportText(text, file.name);
        setImportDialog({ rows, fileName: file.name });
        setImportPreview(null);
        dryRunMutation.mutate(rows);
      } catch (err) {
        alert(`Couldn't parse ${file.name}: ${(err as Error).message}`);
      }
    };
    reader.readAsText(file);
    // reset so re-uploading same file fires onChange again
    e.target.value = "";
  }

  function exportCsv() {
    downloadCsv(
      `schools-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      rows.map((r) => ({
        id: r.id,
        name: r.name,
        postcode: r.postcode ?? "",
        contactEmail: r.contactEmail ?? "",
        contactPhone: r.contactPhone ?? "",
        website: r.website ?? "",
        isActive: r.isActive ? "true" : "false",
        labelCount: r.labelCount,
        lastUpdated: r.lastUpdated ?? "",
      })),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Schools</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length.toLocaleString()} of {totalMatching.toLocaleString()}{" "}
            loaded.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".json,.csv,application/json,text/csv"
            ref={fileInputRef}
            onChange={onImportFileChosen}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" /> Import / refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">
              Search by name (case-insensitive prefix)
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                placeholder="Bedales…"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Postcode prefix</label>
            <Input
              value={postcodePrefix}
              onChange={(e) => setPostcodePrefix(e.target.value)}
              placeholder="GU32"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Status</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1 md:col-span-4">
            <label className="text-xs font-medium text-muted-foreground">Sort</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as (typeof SORTS)[number]["value"])}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        isFetchingMore={query.isFetchingNextPage}
        hasMore={Boolean(query.hasNextPage)}
        onLoadMore={() => void query.fetchNextPage()}
        onRowClick={(r) => navigate({ to: "/schools/$id", params: { id: r.id } })}
        rowKey={(r) => r.id}
        emptyMessage={
          rows.length === 0 && !query.isLoading
            ? "No schools yet. Click Import / refresh to seed from default_schools.json or a DfE-style CSV."
            : "No matching schools."
        }
        {...queryTableProps(query)}
      />

      {importDialog ? (
        <ImportDialog
          fileName={importDialog.fileName}
          rows={importDialog.rows}
          preview={importPreview}
          isPreviewing={dryRunMutation.isPending}
          isCommitting={commitMutation.isPending}
          onCommit={() => commitMutation.mutate(importDialog.rows)}
          onClose={() => {
            setImportDialog(null);
            setImportPreview(null);
            dryRunMutation.reset();
            commitMutation.reset();
          }}
        />
      ) : null}
    </div>
  );
}

function ImportDialog({
  fileName,
  rows,
  preview,
  isPreviewing,
  isCommitting,
  onCommit,
  onClose,
}: {
  fileName: string;
  rows: SchoolImportRow[];
  preview: BulkImportSchoolsOutput | null;
  isPreviewing: boolean;
  isCommitting: boolean;
  onCommit: () => void;
  onClose: () => void;
}) {
  const committed = preview && !preview.dryRun;
  const summary = preview?.summary;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-background/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b p-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Import schools</h2>
            <p className="truncate text-xs text-muted-foreground">
              {fileName} — {rows.length.toLocaleString()} rows
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-5">
          {isPreviewing ? (
            <p className="text-sm text-muted-foreground">Building preview…</p>
          ) : summary ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <SummaryCell label="Received" value={summary.received} tone="neutral" />
                <SummaryCell label="Will create" value={summary.created} tone="success" />
                <SummaryCell label="Will update" value={summary.updated} tone="info" />
                <SummaryCell
                  label="Errors"
                  value={summary.errors.length}
                  tone={summary.errors.length > 0 ? "danger" : "neutral"}
                />
              </div>
              {committed ? (
                <p className="rounded-md border border-success/40 bg-success/5 p-3 text-sm text-success">
                  Import committed. {summary.created.toLocaleString()} created,{" "}
                  {summary.updated.toLocaleString()} updated.
                </p>
              ) : null}
              {summary.errors.length > 0 ? (
                <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
                  <div className="font-medium text-destructive">
                    {summary.errors.length} row(s) skipped
                  </div>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {summary.errors.slice(0, 10).map((e) => (
                      <li key={e.index}>
                        row {e.index}: {e.reason}
                      </li>
                    ))}
                    {summary.errors.length > 10 ? (
                      <li>… and {summary.errors.length - 10} more</li>
                    ) : null}
                  </ul>
                </div>
              ) : null}
              {preview?.preview && preview.preview.length > 0 ? (
                <div className="rounded-md border bg-card/50">
                  <div className="border-b p-2 text-xs font-medium uppercase text-muted-foreground">
                    First {Math.min(20, preview.preview.length)} rows
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="px-3 py-1 text-left">Intent</th>
                        <th className="px-3 py-1 text-left">Doc id</th>
                        <th className="px-3 py-1 text-left">URN</th>
                        <th className="px-3 py-1 text-left">Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 20).map((p) => (
                        <tr key={`${p.index}-${p.docId}`} className="border-b last:border-b-0">
                          <td className="px-3 py-1">
                            <StatusPill tone={p.intent === "create" ? "success" : "info"}>
                              {p.intent}
                            </StatusPill>
                          </td>
                          <td className="px-3 py-1 font-mono">{p.docId}</td>
                          <td className="px-3 py-1 font-mono">{p.urn ?? "—"}</td>
                          <td className="px-3 py-1">{p.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Preview not available.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t bg-card p-4">
          <Button variant="outline" onClick={onClose}>
            {committed ? "Done" : "Cancel"}
          </Button>
          {!committed ? (
            <Button
              onClick={onCommit}
              disabled={
                isPreviewing ||
                isCommitting ||
                !summary ||
                summary.received === 0
              }
            >
              {isCommitting ? "Importing…" : "Import"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "success" | "info" | "danger";
}) {
  const colour = {
    neutral: "text-foreground",
    success: "text-success",
    info: "text-primary",
    danger: "text-destructive",
  }[tone];
  return (
    <div className="rounded-md border bg-card/50 p-3">
      <div className="text-[10px] font-medium uppercase text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${colour}`}>{value.toLocaleString()}</div>
    </div>
  );
}

/**
 * Parse the import file. Accepts:
 *   - JSON object keyed by docId  →  bundled-style ({ "<docKey>": {fields…} })
 *   - JSON array of rows          →  pre-shaped SchoolImportRow[]
 *   - CSV via PapaParse           →  header row required; columns map to fields
 */
function parseImportText(text: string, fileName: string): SchoolImportRow[] {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".json") || text.trim().startsWith("{") || text.trim().startsWith("[")) {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map(toRow).filter(Boolean) as SchoolImportRow[];
    }
    if (parsed && typeof parsed === "object") {
      // Bundled shape: keys are doc ids.
      return Object.entries(parsed as Record<string, unknown>)
        .map(([id, body]) => {
          const row = toRow(body);
          if (!row) return null;
          return { ...row, id } as SchoolImportRow;
        })
        .filter(Boolean) as SchoolImportRow[];
    }
    throw new Error("JSON must be an array or an object");
  }
  // CSV path
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });
  if (result.errors.length > 0) {
    throw new Error(result.errors[0].message);
  }
  return result.data.map((row): SchoolImportRow => {
    return {
      id: row.id || undefined,
      urn: row.urn || row.URN || undefined,
      name: row.name ?? "",
      contact: {
        person: row.contactPerson || row["contact.person"] || undefined,
        email: row.contactEmail || row["contact.email"] || row.email || undefined,
        phone: row.contactPhone || row["contact.phone"] || row.phone || undefined,
      },
      location: {
        street: row.street || row["location.street"] || undefined,
        address: row.address || row["location.address"] || undefined,
        postcode: row.postcode || row["location.postcode"] || undefined,
      },
      website: row.website || undefined,
      logoUrl: row.logoUrl || undefined,
    };
  });
}

function toRow(body: unknown): SchoolImportRow | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (!b.name) return null;
  return {
    id: typeof b.id === "string" ? b.id : undefined,
    urn: typeof b.urn === "string" ? b.urn : undefined,
    name: b.name as string,
    contact: b.contact as SchoolImportRow["contact"],
    location: b.location as SchoolImportRow["location"],
    website: typeof b.website === "string" ? b.website : undefined,
    logoUrl: typeof b.logoUrl === "string" ? b.logoUrl : undefined,
  };
}
